import { hasGoogleSession, clearGoogleSession } from "./googleSession";
import {restoreCustomerCart} from "./restoreCart";
import {quoteCart,getProductInfo} from "../api/shop";
import { useEffect } from "react";
import { useSelector, useDispatch, useStore, batch } from "react-redux";

import {
  setUserLogged,
  setSessionReady,
  setUserData,
  setLoading,
  updateShopProducts,
  updateShopCategories,
  setCorporationData,
  updateCart,
  setUserLogOut,
  setAlert,
} from "../reducers/userConfigSlice";

import { apiRest } from "../api/constants";
import { getCustomerInfo } from "../api/profile";
import { getCategories, getProducts } from "../api/shop";
import { addIdKeyIfMissing } from "@delibrandev/utils/src/function";
import { getCorporationInfo, logout } from "../api/requests";
import { readLoginLS, removeLoginLS } from "../functions/rememberedLogin";
import { logIn } from "../api/login";
import { isShopLogoutEvent, logoutRevision } from "./shopSession";

const Initializer = ({ onBootstrap } = {}) => {
  const dispatch = useDispatch();
  const store = useStore();

  const { componentActive } = useSelector((state) => state.aplicationConfig.userConfig);

  // ✅ Get Coporation Data
  const init = async (corporatioData) => {
    try {
      const [productsRes, categoriesRes, corporationRes] = await Promise.all([getProducts({}), getCategories({})]);

      const currentCart=store.getState().aplicationConfig.userConfig.cart;
      if(productsRes && !productsRes.error && currentCart?.ShoppingCartProducts?.length) {
        const visible=new Set(productsRes.data.products.filter(p=>p.status==="PUBLISHED").map(p=>p.productId));
        const recovered=await Promise.all(currentCart.ShoppingCartProducts.map(async line=>{
          if(!visible.has(line.productId)) return {...line,unavailable:true};
          try {
            const [detail,quote]=await Promise.all([getProductInfo(line.productId),quoteCart({lines:[{productId:line.productId,quantity:line.quantity,variantId:line.variantId,itemIds:line.itemIds||[],note:line.note}]})]);
            return {...line,Product:detail?.error===false?(Array.isArray(detail.data)?detail.data[0]:detail.data):line.Product,unavailable:quote?.error!==false};
          } catch {return {...line,unavailable:true};}
        }));
        dispatch(updateCart({...currentCart,ShoppingCartProducts:recovered}));
      }
      // Batching: agrupa varios dispatch en un solo ciclo de render
      batch(() => {
        dispatch(setCorporationData(corporatioData));

        //dispatch(setUserData(userData));

        if (productsRes && !productsRes.error) {
          dispatch(updateShopProducts(addIdKeyIfMissing(productsRes.data.products.filter((f) => f.status !== "DRAFT" && f.status !== "INACTIVE"))));
        }

        if (categoriesRes && !categoriesRes.error) {
          dispatch(updateShopCategories(addIdKeyIfMissing(categoriesRes.data.filter((f) => f.isActive === true))));
        }

        if (corporationRes && !corporationRes.error) {
          dispatch(setCorporationData(corporationRes.data));
        }
      });
    } catch (err) {
      console.error("[init] Error general al inicializar datos:", err);
    }
  };

  // ✅ Get Session Data - Independientes de los get de la empresa
  const initSession = async (revision) => {
    try {
      const customerRes=await getCustomerInfo({});
      if(revision!==logoutRevision())return;
      const recovered=customerRes?.error===false?await restoreCustomerCart(customerRes.data.customerId,store.getState().aplicationConfig.userConfig.cart):null;

      if (revision !== logoutRevision()) return;
      // Batching: agrupa varios dispatch en un solo ciclo de render
      batch(() => {
        if (customerRes && !customerRes.error) {
          dispatch(setUserData(customerRes.data));
          dispatch(setUserLogged(true));
          if(recovered)dispatch(updateCart(recovered));
        } else {
          dispatch(setUserLogged(false));
        }
      });
      return customerRes?.error === false;
    } catch (err) {
      dispatch(setUserLogged(false));
      console.error("[initSession] Error general al inicializar datos:", err);
      return false;
    }
  };

  const autoLogin = async (revision) => {
    if (hasGoogleSession()) {
      if (!await initSession(revision)) clearGoogleSession();
      return;
    }
    const savedLogin = readLoginLS(apiRest);
    if (savedLogin?.email && savedLogin?.password) {
      dispatch(setLoading(true));
      const data = {
        email: savedLogin.email,
        password: savedLogin.password,
      };
      const response = await logIn(data);
      if (revision !== logoutRevision()) {
        if (response && !response.error) await logout();
        return;
      }
      if (response && !response.error) {
        await initSession(revision);
        dispatch(setLoading(false));
      } else {
        dispatch(setUserLogged(false));
        dispatch(setLoading(false));
      }
    } else {
      dispatch(setUserLogged(false));
    }
  };

  // ✅ Efecto principal de inicialización: control de sesión + carga de datos
  useEffect(() => {
    const revision = logoutRevision();
    const receiveLogout = (event) => {
      if (!isShopLogoutEvent(event)) return;
      clearGoogleSession();
      removeLoginLS(apiRest);
      dispatch(setUserLogOut());
      dispatch(setAlert({ type: "info", title: "Sesión cerrada", message: "Se ha cerrado la sesión en otra pestaña de esta tienda." }));
    };
    window.addEventListener("storage", receiveLogout);
    const initializeApp = async () => {
      let themeReady = false;
      console.log("| 🚀 Inicializando aplicación...");

      try {
        // 1) PRIMERA LLAMADA: getCorporationInfo
        const corporationRes = await getCorporationInfo();
        if (revision !== logoutRevision()) return;

        // 2) Si la plataforma no existe -> redirección y cortar ejecución
        if (corporationRes?.error === true && corporationRes?.message === "Platform not found.") {
          window.location.assign("https://delibran.com/");
          return; // Importante: no seguimos
        } else {
          if (corporationRes?.error !== false || !corporationRes.data) throw new Error("Shop configuration unavailable");
          // 3) Autologin + datos empresa
          dispatch(setCorporationData(corporationRes.data));
          await init(corporationRes.data);
          themeReady = true;
          onBootstrap?.("ready");
          await autoLogin(revision);
        }
      } catch (e) {
        if (!themeReady) onBootstrap?.("error");
        dispatch(setUserLogged(false));
        console.error("[initializeApp] Error en inicialización:", e);
      } finally {
        dispatch(setLoading(false));
        dispatch(setSessionReady(true));
      }
    };

    /* const initializeApp = async () => {
      console.log("| 🚀 Inicializando aplicación...");
      dispatch(setLoading(true));

      const sessionIsValid = await autoLogin(revision);
      await init();
      if (sessionIsValid) await initSession();

      dispatch(setLoading(false));
      onInit();
    };*/

    initializeApp();
    return () => window.removeEventListener("storage", receiveLogout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Scroll al cargar cada componente activo
  useEffect(() => {
    document.getElementById("adminBody")?.scrollTo(0, 0);
  }, [componentActive]);

  return null;
};

export default Initializer;
